/**
 * FNSP (Family and Neighborhood Support Partnership) Eligibility Types
 */

// FNSP Eligibility Status Response (from eligibility check endpoint)
export interface FNSPEligibilityStatus {
  hasAssessment: boolean;
  isEligible: boolean;
  eligibilityReason: string | null;
  assessmentDate: string | null;
  riskFactorCount: number;
  inServiceArea: boolean;
  hasChildUnder18OrDisabled21: boolean;
  conductedByStaffID: number | null;
  conductedByStaffName: string | null;
}

// Full FNSP Assessment data
export interface FNSPAssessment {
  fnspEligibilityID: number;
  individualID: number;

  // Question 0: Required for eligibility
  hasChildUnder18OrDisabled21: boolean;

  // Questions 1-15: Risk factors
  affectedByPoverty: boolean;          // Q1
  homelessOrUnstable: boolean;         // Q2
  parentIncarcerated: boolean;         // Q3
  immigrationChallenges: boolean;      // Q4
  familyConflictOrDV: boolean;         // Q5
  childWelfareInvolved: boolean;       // Q6
  sexualAbuseOrTrafficking: boolean;   // Q7
  significantDisability: boolean;      // Q8
  lgbtq: boolean;                      // Q9
  academicFailure: boolean;            // Q10
  excessiveAbsences: boolean;          // Q11
  schoolBehaviorProblems: boolean;     // Q12
  juvenileJustice: boolean;            // Q13
  mentalHealthSymptoms: boolean;       // Q14
  substanceAbuse: boolean;             // Q15

  // Calculated fields
  isEligible: boolean;
  eligibilityReason: string | null;

  // Audit fields
  conductedByStaffID: number | null;
  conductedByStaffName: string | null;
  assessmentDate: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  zipCode: string | null;
}

// Request payload for creating/updating FNSP assessment
export interface FNSPAssessmentRequest {
  hasChildUnder18OrDisabled21: boolean;
  affectedByPoverty: boolean;
  homelessOrUnstable: boolean;
  parentIncarcerated: boolean;
  immigrationChallenges: boolean;
  familyConflictOrDV: boolean;
  childWelfareInvolved: boolean;
  sexualAbuseOrTrafficking: boolean;
  significantDisability: boolean;
  lgbtq: boolean;
  academicFailure: boolean;
  excessiveAbsences: boolean;
  schoolBehaviorProblems: boolean;
  juvenileJustice: boolean;
  mentalHealthSymptoms: boolean;
  substanceAbuse: boolean;
}

// Question configuration from backend
export interface FNSPQuestion {
  sequence: number;
  field: keyof FNSPAssessmentRequest;
  question: string;
  required: boolean;
}

// Response from questions endpoint
export interface FNSPQuestionsResponse {
  questions: FNSPQuestion[];
  eligibilityRules: {
    requireQuestion0: boolean;
    minRiskFactors: number;
    requireServiceAreaZip: boolean;
  };
}

// Default/empty assessment for form initialization
export const DEFAULT_FNSP_ASSESSMENT: FNSPAssessmentRequest = {
  hasChildUnder18OrDisabled21: false,
  affectedByPoverty: false,
  homelessOrUnstable: false,
  parentIncarcerated: false,
  immigrationChallenges: false,
  familyConflictOrDV: false,
  childWelfareInvolved: false,
  sexualAbuseOrTrafficking: false,
  significantDisability: false,
  lgbtq: false,
  academicFailure: false,
  excessiveAbsences: false,
  schoolBehaviorProblems: false,
  juvenileJustice: false,
  mentalHealthSymptoms: false,
  substanceAbuse: false,
};

// FNSP Questions with their full text
export const FNSP_QUESTIONS: FNSPQuestion[] = [
  {
    sequence: 0,
    field: 'hasChildUnder18OrDisabled21',
    question: 'Is there a child under 18 years old or under 21 years old with a disability in the household?',
    required: true,
  },
  {
    sequence: 1,
    field: 'affectedByPoverty',
    question: 'Child or youth growing up in a neighborhood that is affected by poverty, crime, unemployment, and/or community violence and trauma',
    required: false,
  },
  {
    sequence: 2,
    field: 'homelessOrUnstable',
    question: 'Families or youth experiencing homelessness or chronic housing instability',
    required: false,
  },
  {
    sequence: 3,
    field: 'parentIncarcerated',
    question: 'Parent or primary caregiver is incarcerated',
    required: false,
  },
  {
    sequence: 4,
    field: 'immigrationChallenges',
    question: 'Child or youth experiencing immigration challenges and need legal advocacy',
    required: false,
  },
  {
    sequence: 5,
    field: 'familyConflictOrDV',
    question: 'Child or youth exposed to family conflict or domestic violence',
    required: false,
  },
  {
    sequence: 6,
    field: 'childWelfareInvolved',
    question: 'Child or youth involved in or transitioning out of the child welfare system',
    required: false,
  },
  {
    sequence: 7,
    field: 'sexualAbuseOrTrafficking',
    question: 'Child or youth affected by sexual abuse and/or human trafficking',
    required: false,
  },
  {
    sequence: 8,
    field: 'significantDisability',
    question: 'Child, youth or parent living with a significant disability that impacts the family',
    required: false,
  },
  {
    sequence: 9,
    field: 'lgbtq',
    question: 'Child or youth is lesbian, gay, bisexual, transgender or questioning (LGBTQ)',
    required: false,
  },
  {
    sequence: 10,
    field: 'academicFailure',
    question: 'Child or youth has failed an academic course for two or more grading periods in the last year',
    required: false,
  },
  {
    sequence: 11,
    field: 'excessiveAbsences',
    question: 'Child or youth has had 15 or more unexcused absences in the last year',
    required: false,
  },
  {
    sequence: 12,
    field: 'schoolBehaviorProblems',
    question: 'Child or youth has gotten in trouble at school for serious behavior problems like fighting, stealing or property destruction',
    required: false,
  },
  {
    sequence: 13,
    field: 'juvenileJustice',
    question: 'Child or youth has been arrested or involved in the juvenile justice system',
    required: false,
  },
  {
    sequence: 14,
    field: 'mentalHealthSymptoms',
    question: 'Child, youth or parent/caregiver report symptoms of mental health diagnoses (i.e. anxiety, depression, stress)',
    required: false,
  },
  {
    sequence: 15,
    field: 'substanceAbuse',
    question: 'Child, youth or parent/caregiver has problems with substance abuse',
    required: false,
  },
];
